<template>
  <div id="feasibilityCheckModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title mb-0">New feasibility check</h5>
            <div class="fz-muted small">Queues a check in Snowflake. Results appear in the table in 1–10 minutes.</div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- what to check -->
          <div class="fz-segment mb-3" role="tablist">
            <button type="button" class="fz-segment-btn" :class="{ active: mode === 'study' }" @click="mode = 'study'">
              An existing study
            </button>
            <button type="button" class="fz-segment-btn" :class="{ active: mode === 'campaign' }" @click="mode = 'campaign'">
              A campaign with places or pixels
            </button>
          </div>

          <div class="mb-3">
            <label class="form-label">Customer <span v-if="mode === 'study'" class="fz-muted">(optional filter)</span></label>
            <select class="form-select" v-model="customerId" @change="onCustomerChange">
              <option :value="null">{{ mode === 'study' ? 'All customers' : 'Choose a customer' }}</option>
              <option v-for="customer in model.customers" :key="customer.customerId" :value="customer.customerId">
                {{ customer.customerName }}
              </option>
            </select>
          </div>

          <!-- study -->
          <template v-if="mode === 'study'">
            <label class="form-label">Study</label>
            <div class="input-group mb-2">
              <span class="input-group-text bg-white"><font-awesome-icon :icon="['fas', 'magnifying-glass']" class="fz-muted" /></span>
              <input type="text" class="form-control" placeholder="Search by study ID or name" v-model="studySearch">
            </div>
            <div class="fz-study-list border rounded">
              <div v-if="!studiesLoaded" class="p-3 fz-muted">Loading studies…</div>
              <div v-else-if="filteredStudies.length === 0" class="p-3 fz-muted">No studies match.</div>
              <button v-for="study in visibleStudies" :key="study.attributionId" type="button"
                      class="fz-study-item" :class="{ selected: study.attributionId === studyId }"
                      @click="studyId = study.attributionId">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="me-2">
                    <div class="fw-semibold">{{ study.studyName }}</div>
                    <div class="small fz-muted">
                      #{{ study.attributionId }} · {{ study.customerName || 'Customer ' + study.customerId }}
                      · {{ formatEpoch(study.startDate) }} – {{ formatEpoch(study.endDate) }}
                    </div>
                  </div>
                  <span class="badge rounded-pill fz-type-badge">{{ studyTypeLabel(study.studyType) }}</span>
                </div>
              </button>
              <div v-if="filteredStudies.length > visibleStudies.length" class="p-2 small fz-muted text-center">
                Showing {{ visibleStudies.length }} of {{ filteredStudies.length }} — refine the search to see more.
              </div>
            </div>
          </template>

          <!-- campaign + places / pixels -->
          <template v-else>
            <div v-if="!customerId" class="alert alert-light border small mb-0">
              Choose a customer to pick its campaigns, places and pixels.
            </div>
            <template v-else>
              <div class="mb-3">
                <label class="form-label">Campaigns</label>
                <select-dropdown :key="'campaigns-' + customerId" :list="campaignOptions" label-field="label"
                                 list-title="Campaigns" @update:selectedItems="items => selectedCampaigns = items"></select-dropdown>
              </div>

              <label class="form-label d-block">Conversions</label>
              <div class="fz-segment fz-segment-sm mb-2">
                <button type="button" class="fz-segment-btn" :class="{ active: targetType === 'places' }" @click="targetType = 'places'">
                  Store visits
                </button>
                <button type="button" class="fz-segment-btn" :class="{ active: targetType === 'pixels' }" @click="targetType = 'pixels'">
                  Website pixel
                </button>
              </div>
              <div v-if="targetType === 'places'">
                <select-dropdown :key="'places-' + customerId" :list="placeOptions" label-field="label" list-title="Places"
                                 @update:selectedItems="items => selectedPlaces = items"></select-dropdown>
                <div v-if="placesError" class="form-text text-danger">{{ placesError }}</div>
                <div v-else class="form-text">{{ placesLoading ? 'Loading places…' : placeOptions.length + ' places for this customer' }}</div>
              </div>
              <div v-else>
                <select-dropdown :key="'pixels-' + customerId" :list="pixelOptions" label-field="label" list-title="Pixels"
                                 @update:selectedItems="items => selectedPixels = items"></select-dropdown>
                <div v-if="pixelsError" class="form-text text-danger">{{ pixelsError }}</div>
                <div v-else class="form-text">{{ pixelsLoading ? 'Loading pixels…' : pixelOptions.length + ' pixels for this customer' }}</div>
              </div>
            </template>
          </template>

          <!-- optional dates -->
          <div class="mt-3">
            <a href="javascript:void(0)" class="small" @click="showDates = !showDates">
              {{ showDates ? 'Hide' : 'Set' }} flight dates <span class="fz-muted">(optional)</span>
            </a>
            <div v-if="showDates" class="row g-2 mt-1">
              <div class="col-6">
                <label class="form-label small mb-1">Flight start</label>
                <input type="date" class="form-control" v-model="flightStart">
              </div>
              <div class="col-6">
                <label class="form-label small mb-1">Flight end</label>
                <input type="date" class="form-control" v-model="flightEnd">
              </div>
              <div class="form-text">Defaults to the study's dates, else the campaigns' flights.</div>
            </div>
          </div>

          <div v-if="errorText" class="alert alert-warning mt-3 mb-0">{{ errorText }}</div>
        </div>

        <div class="modal-footer">
          <span class="me-auto small fz-muted" v-if="summary">{{ summary }}</span>
          <button type="button" class="btn btn-light border" @click="hideModal" :disabled="submitting">Cancel</button>
          <button type="button" class="btn btn-primary" @click="onSubmit" :disabled="!canSubmit">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
            Queue check
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global bootstrap */
import streetmetrics from '@/model';
import SelectDropdown from './SelectDropdown';
import AttributionService from '../services/AttributionService';
import { errorMessage, unwrap } from '../utils/feasibility';

const MAX_VISIBLE_STUDIES = 100;

export default {
  name: 'FeasibilityCheckModal',
  components: { SelectDropdown },
  props: {
    studies: { type: Array, default: () => [] },
    studiesLoaded: { type: Boolean, default: false },
    initialCustomerId: { type: Number, default: null },
  },
  emits: ['queued', 'closed'],
  data() {
    return {
      model: streetmetrics.model,
      mode: 'study',
      customerId: this.initialCustomerId,
      studySearch: '',
      studyId: null,
      campaignOptions: [],
      selectedCampaigns: [],
      targetType: 'places',
      placeOptions: [],
      placesLoading: false,
      selectedPlaces: [],
      pixelOptions: [],
      pixelsLoading: false,
      pixelsError: '',
      placesError: '',
      selectedPixels: [],
      showDates: false,
      flightStart: '',
      flightEnd: '',
      submitting: false,
      errorText: null,
      modalElement: null,
      currentModal: null,
    };
  },
  computed: {
    filteredStudies() {
      const terms = this.studySearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
      return this.studies.filter((study) => {
        if (this.customerId && study.customerId !== this.customerId) return false;
        const text = `${study.attributionId} ${study.studyName || ''} ${study.customerName || ''}`.toLowerCase();
        return terms.every((term) => text.includes(term));
      });
    },
    visibleStudies() {
      return this.filteredStudies.slice(0, MAX_VISIBLE_STUDIES);
    },
    selectedStudy() {
      return this.studies.find((study) => study.attributionId === this.studyId) || null;
    },
    canSubmit() {
      if (this.submitting) return false;
      if (this.mode === 'study') return Boolean(this.studyId);
      const targets = this.targetType === 'places' ? this.selectedPlaces : this.selectedPixels;
      return Boolean(this.customerId) && this.selectedCampaigns.length > 0 && targets.length > 0;
    },
    summary() {
      if (this.mode === 'study') return this.selectedStudy ? `Study #${this.selectedStudy.attributionId} selected` : '';
      const targets = this.targetType === 'places' ? this.selectedPlaces.length : this.selectedPixels.length;
      if (!this.selectedCampaigns.length && !targets) return '';
      return `${this.selectedCampaigns.length} campaign(s) · ${targets} ${this.targetType === 'places' ? 'place' : 'pixel'}(s)`;
    },
  },
  mounted() {
    this.modalElement = document.getElementById('feasibilityCheckModal');
    this.currentModal = new bootstrap.Modal(this.modalElement);
    this.modalElement.addEventListener('hidden.bs.modal', this.onModalHidden);
    this.currentModal.show();
    if (this.customerId) this.onCustomerChange();
  },
  beforeUnmount() {
    if (this.modalElement) this.modalElement.removeEventListener('hidden.bs.modal', this.onModalHidden);
  },
  methods: {
    async onCustomerChange() {
      if (this.selectedStudy && this.customerId && this.selectedStudy.customerId !== this.customerId) this.studyId = null;
      this.selectedCampaigns = [];
      this.selectedPlaces = [];
      this.selectedPixels = [];
      this.campaignOptions = (this.model.campaigns || [])
        .filter((campaign) => campaign.customerId === this.customerId)
        .map((campaign) => ({ ...campaign, label: `${campaign.campaignId} – ${campaign.campaignRef}`, selected: false }));
      this.placeOptions = [];
      this.pixelOptions = [];
      this.placesError = '';
      this.pixelsError = '';
      if (!this.customerId) return;
      const customerId = this.customerId;
      this.placesLoading = true;
      this.pixelsLoading = true;
      new AttributionService().getFeasibilityPlaces(customerId)
        .then((response) => {
          if (customerId !== this.customerId) return;
          this.placeOptions = (unwrap(response) || []).map((place) => ({
            ...place,
            label: `${place.placeId} – ${place.displayName || place.placeRef}${place.placeCity ? ', ' + place.placeCity : ''}`,
            selected: false,
          }));
        })
        .catch((error) => { this.placesError = errorMessage(error, 'Places could not be loaded.'); })
        .finally(() => { this.placesLoading = false; });
      new AttributionService().getFeasibilityPixels(customerId)
        .then((response) => {
          if (customerId !== this.customerId) return;
          this.pixelOptions = (unwrap(response) || []).map((pixel) => ({
            ...pixel,
            label: `${pixel.pixelId} – ${(pixel.metaData && (pixel.metaData.displayName || pixel.metaData.page)) || pixel.pixelUUID || ''}`,
            selected: false,
          }));
        })
        .catch((error) => { this.pixelsError = errorMessage(error, 'Pixels could not be loaded.'); })
        .finally(() => { this.pixelsLoading = false; });
    },
    buildRequest() {
      const body = {};
      if (this.mode === 'study') {
        body.studyId = this.studyId;
        if (this.selectedStudy && this.selectedStudy.customerId) body.customerId = this.selectedStudy.customerId;
      } else {
        body.customerId = this.customerId;
        body.campaignIds = this.selectedCampaigns.map((campaign) => campaign.campaignId);
        if (this.targetType === 'places') body.placeIds = this.selectedPlaces.map((place) => place.placeId);
        else body.pixelIds = this.selectedPixels.map((pixel) => pixel.pixelId);
      }
      if (this.showDates && this.flightStart) body.flightStart = this.flightStart;
      if (this.showDates && this.flightEnd) body.flightEnd = this.flightEnd;
      return body;
    },
    async onSubmit() {
      this.errorText = null;
      this.submitting = true;
      try {
        const started = unwrap(await new AttributionService().startFeasibility(this.buildRequest()));
        this.$emit('queued', started.requestId);
        this.hideModal();
      } catch (error) {
        this.errorText = errorMessage(error, 'The check could not be queued.');
      } finally {
        this.submitting = false;
      }
    },
    hideModal() {
      if (this.currentModal) this.currentModal.hide();
      else this.$emit('closed');
    },
    onModalHidden() {
      this.$emit('closed');
    },
    studyTypeLabel(type) {
      const t = String(type || '').toUpperCase();
      if (t === 'WEB') return 'Web';
      if (t === 'FOOTFALL' || t === 'LOCATION') return 'Footfall';
      return type || '';
    },
    formatEpoch(epoch) {
      if (!epoch) return '—';
      return new Date(epoch * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    },
  },
};
</script>

<style scoped>
.fz-segment {
  display: inline-flex;
  background: #eef1f4;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}
.fz-segment-btn {
  border: 0;
  background: transparent;
  padding: 6px 14px;
  border-radius: 6px;
  color: #4a5966;
  font-weight: 500;
}
.fz-segment-btn.active {
  background: #fff;
  color: #052f49;
  box-shadow: 0 1px 2px rgba(5, 47, 73, 0.15);
}
.fz-segment-sm .fz-segment-btn {
  padding: 4px 12px;
  font-size: 0.875rem;
}
.fz-study-list {
  max-height: 300px;
  overflow-y: auto;
}
.fz-study-item {
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  border-bottom: 1px solid #eef1f4;
  background: #fff;
  padding: 10px 12px;
}
.fz-study-item:hover {
  background: #f6f8fa;
}
.fz-study-item.selected {
  background: #e8f0f6;
  box-shadow: inset 3px 0 0 #052f49;
}
.fz-type-badge {
  background: #eef1f4;
  color: #4a5966;
  font-weight: 500;
}
</style>
